import { useCallback, useState } from 'react';
import { useForm } from 'src/components/Form';
import ModalDialog, { ModalCloseHandler } from 'src/components/ModalDialog';
import Select from 'src/components/Select/Select';
import * as Yup from 'yup';
import {
  SolutionDeviceGroupDetails,
  SolutionDeviceGroupItemField,
  SolutionDeviceGroupPropertyItem,
} from '../../types';
import { useSolutionDeviceGroupEditFormFields } from '../hooks';
import SolutionDeviceGroupFilterHeading from './SolutionDeviceGroupFilterHeading';
import SolutionDeviceGroupEditFormRow from './SolutionDeviceGroupEditFormRow';
import { DEVICE_GROUP_FILTER_CONDITION_OPTIONS } from '../../SolutionDevices/constants';

type Props = {
  item?: SolutionDeviceGroupDetails;
  properties?: SolutionDeviceGroupPropertyItem[];
  confirmBtnText?: string;
  disabled?: boolean;
  loading?: boolean;
  open: boolean;
  onSubmit?: (item?: SolutionDeviceGroupDetails) => void;
  onClose: ModalCloseHandler;
  title: string;
};

export default function SolutionDeviceGroupEditModal({
  item,
  disabled,
  loading,
  open,
  onClose,
  onSubmit,
  title,
  confirmBtnText = 'Save',
}: Props) {
  const [mainFields, filterFields] = useSolutionDeviceGroupEditFormFields(item);
  const [filters, setFilters] = useState<any[]>(item?.filters ?? []);

  const onChange = useCallback(({ filters }: SolutionDeviceGroupDetails) => {
    setFilters(filters);
  }, []);

  const [form, handleSubmit] = useForm({
    fields: [
      ...mainFields,
      {
        type: 'heading',
        name: 'filterHeading',
        label: (
          <>
            <SolutionDeviceGroupFilterHeading />
          </>
        ),
      },
      ...(filters?.length > 1
        ? [
            {
              name: SolutionDeviceGroupItemField.FiltersOperator,
              label: 'Apply if',
              placeholder: 'Select the value',
              Component: Select,
              options: DEVICE_GROUP_FILTER_CONDITION_OPTIONS,
              initialValue: 'AND',
              validation: Yup.string().required('Operator is required'),
              required: true,
              sx: { width: ({ spacing }) => spacing(30) },
            },
          ]
        : []),
      {
        name: SolutionDeviceGroupItemField.Filters,
        appendLabel: 'Filter',
        childFields: filterFields,
        RowComponent: SolutionDeviceGroupEditFormRow,
      },
    ],
    onSubmit: (values) => onSubmit && onSubmit(values),
    values: item,
    onChange,
    disabled,
  });

  return (
    <ModalDialog
      open={open}
      title={title}
      onClose={onClose}
      confirmBtnText={confirmBtnText}
      onConfirm={disabled ? onClose : handleSubmit}
      loading={loading}
      showCancelBtn={!disabled}
      maxWidth="md"
      fullWidth
    >
      {form}
    </ModalDialog>
  );
}
